import { useMemo } from "react";
import {
  useGetStoredSelectedBranches,
  useGetStoredSelectedSegments,
  useGetStoredSelectedClients,
  useGetStoredSelectedSuppliers,
  useGetStoredSelectedVehicles,
  useGetStoredSelectedDrivers,
  useGetStoredIncludeNullBranch,
  useGetStoredIncludeNullSegment,
  useGetStoredIncludeNullClient,
  useGetStoredIncludeNullSupplier,
  useGetStoredIncludeNullVehicle,
  useGetStoredIncludeNullDriver,
} from "@store/tmsAssetTree/tmsAssetTreeSlice";
import type { DashboardFilter } from "../types/dashboard";

export interface StoredFilterValues {
  selectedBranches: string[];
  selectedSegments: string[];
  selectedClients: string[];
  selectedSuppliers: string[];
  selectedVehicles: string[];
  selectedDrivers: string[];
  includeNullBranch: boolean;
  includeNullSegment: boolean;
  includeNullClient: boolean;
  includeNullSupplier: boolean;
  includeNullVehicle: boolean;
  includeNullDriver: boolean;
}

export const applyStoredFilters = <T extends DashboardFilter>(base: T, stored: StoredFilterValues): T => {
  const payload: DashboardFilter = { ...base };

  if (stored.selectedBranches?.length) payload.branchIds = stored.selectedBranches;
  if (stored.selectedSegments?.length) payload.segmentIds = stored.selectedSegments;
  if (stored.selectedClients?.length) payload.clientIds = stored.selectedClients;
  if (stored.selectedSuppliers?.length) payload.supplierIds = stored.selectedSuppliers;
  if (stored.selectedVehicles?.length) payload.vehicleIds = stored.selectedVehicles;
  if (stored.selectedDrivers?.length) payload.driverIds = stored.selectedDrivers;
  payload.includeNullBranch = stored.includeNullBranch;
  payload.includeNullSegment = stored.includeNullSegment;
  payload.includeNullClient = stored.includeNullClient;
  payload.includeNullSupplier = stored.includeNullSupplier;
  payload.includeNullVehicle = stored.includeNullVehicle;
  payload.includeNullDriver = stored.includeNullDriver;

  return payload as T;
};

export const toOptionalListFilters = (stored: StoredFilterValues): DashboardFilter => ({
  branchIds: stored.selectedBranches?.length ? stored.selectedBranches : undefined,
  segmentIds: stored.selectedSegments?.length ? stored.selectedSegments : undefined,
  clientIds: stored.selectedClients?.length ? stored.selectedClients : undefined,
  supplierIds: stored.selectedSuppliers?.length ? stored.selectedSuppliers : undefined,
  vehicleIds: stored.selectedVehicles?.length ? stored.selectedVehicles : undefined,
  driverIds: stored.selectedDrivers?.length ? stored.selectedDrivers : undefined,
  includeNullBranch: stored.includeNullBranch,
  includeNullSegment: stored.includeNullSegment,
  includeNullClient: stored.includeNullClient,
  includeNullSupplier: stored.includeNullSupplier,
  includeNullVehicle: stored.includeNullVehicle,
  includeNullDriver: stored.includeNullDriver,
});

export const useStoredFilterValues = (): StoredFilterValues => {
  const selectedBranches = useGetStoredSelectedBranches();
  const selectedSegments = useGetStoredSelectedSegments();
  const selectedClients = useGetStoredSelectedClients();
  const selectedSuppliers = useGetStoredSelectedSuppliers();
  const selectedVehicles = useGetStoredSelectedVehicles();
  const selectedDrivers = useGetStoredSelectedDrivers();
  const includeNullBranch = useGetStoredIncludeNullBranch();
  const includeNullSegment = useGetStoredIncludeNullSegment();
  const includeNullClient = useGetStoredIncludeNullClient();
  const includeNullSupplier = useGetStoredIncludeNullSupplier();
  const includeNullVehicle = useGetStoredIncludeNullVehicle();
  const includeNullDriver = useGetStoredIncludeNullDriver();

  return useMemo(
    () => ({
      selectedBranches,
      selectedSegments,
      selectedClients,
      selectedSuppliers,
      selectedVehicles,
      selectedDrivers,
      includeNullBranch,
      includeNullSegment,
      includeNullClient,
      includeNullSupplier,
      includeNullVehicle,
      includeNullDriver,
    }),
    [
      selectedBranches,
      selectedSegments,
      selectedClients,
      selectedSuppliers,
      selectedVehicles,
      selectedDrivers,
      includeNullBranch,
      includeNullSegment,
      includeNullClient,
      includeNullSupplier,
      includeNullVehicle,
      includeNullDriver,
    ]
  );
};

export const useRequestFilters = (stored: StoredFilterValues, startDate: string, endDate: string): DashboardFilter =>
  useMemo(() => applyStoredFilters<DashboardFilter>({ startDate, endDate }, stored), [stored, startDate, endDate]);
